import { create } from 'zustand';

import { settingsApi } from '@core/settings/settings.api';
import { EMPTY_SETTINGS, HISTORY_LIMIT, type SettingsDocument } from '@core/settings/settings.model';

/**
 * The saved organisation settings - scoped flags, formats, rules, role
 * defaults - as the rest of the app reads them.
 *
 * Loaded once before the first render (main.tsx), so a feature switched off
 * at a user's scope is never drawn and then withdrawn. Screens do not read
 * this directly: flags reach them through `useCan` / `useFlag`, formats and
 * rules through the valuation profile, role defaults through
 * `useRoleDefault`. The Settings pages keep their unsaved edits elsewhere and
 * only write here on Save.
 */

export type SettingsStatus = 'loading' | 'ready' | 'failed';

interface SettingsState {
  document: SettingsDocument;
  status: SettingsStatus;
  setDocument: (document: SettingsDocument) => void;
  /** Persists `next`, recording `changes` against `by` in the history. */
  save: (next: SettingsDocument, changes: string[], by: string) => Promise<void>;
}

export const useSettingsStore = create<SettingsState>((set) => ({
  document: EMPTY_SETTINGS,
  status: 'loading',

  setDocument: (document) => set({ document, status: 'ready' }),

  save: async (next, changes, by) => {
    const entry = { at: new Date().toISOString(), by, changes };
    const withHistory: SettingsDocument = { ...next, history: [entry, ...next.history].slice(0, HISTORY_LIMIT) };
    const saved = await settingsApi.save(withHistory);

    set({ document: saved, status: 'ready' });
  }
}));

const SETTINGS_TIMEOUT_MS = 2500;

/**
 * Reads the settings before the first render. Time-boxed and fail-open: a slow
 * or failing Settings API costs a console warning and the shipped behaviour,
 * never a blank portal.
 */
export async function loadSettings(): Promise<void> {
  try {
    const document = await Promise.race([
      settingsApi.load(),
      new Promise<never>((_, reject) => setTimeout(() => reject(new Error('timed out')), SETTINGS_TIMEOUT_MS))
    ]);

    useSettingsStore.getState().setDocument(document);
  } catch (error) {
    console.warn('[settings] could not be loaded; running on the shipped defaults.', error);
    useSettingsStore.setState({ document: EMPTY_SETTINGS, status: 'failed' });
  }
}
